import React, { useState, useEffect } from "react";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import authFetch from "../axios";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [recentDevices, setRecentDevices] = useState([]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const res = await authFetch.get("/dashboard");
        if (res.data.success) {
          setData(res.data);
        }
      } catch (err) {
        console.error("Error fetching dashboard data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setDevicesLoading(true);
        const res = await authFetch.get('get-device', { params: { page: 1, perPage: 5 } });
        const devices = res.data?.data ?? [];
        setRecentDevices(devices.slice(0, 5));
      } catch (err) {
        console.error('Error fetching devices', err);
      } finally {
        setDevicesLoading(false);
      }
    };
    fetchDevices();
  }, []);

  const formatNumber = (num) => {
     return num?.toLocaleString() ?? "—";
  };

  const usagePercentage = data ? data.license.utilization_pct : 0;
  const isExpired = data ? data.integrity.status === "EXPIRED" : false;

  const commonOptions = {
    cutout: "75%",
    plugins: {
      legend: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  const distributionData = {
    labels: ["Used/Onboarded", "Remaining"],
    datasets: [
      {
        data: data
          ? [
              data.utilization_distribution.used,
              data.utilization_distribution.remaining,
            ]
          : [0, 1],
        backgroundColor: ["#3B82F6", "#E2E8F0"],
        hoverBackgroundColor: ["#2563EB", "#CBD5E1"],
        borderWidth: 0,
      },
    ],
  };

  const serialNumberTemplate = (rowData, options) => {
    return options.rowIndex + 1;
  };

  return (
    <div className="surface-ground px-4 py-5 md:px-6 lg:px-8 w-full">
      <div className="grid">
        <div className="col-12 mb-5">
          <div className="flex align-items-center justify-content-between">
            <div>
              <span className="text-900 text-3xl font-medium">
                License Overview
              </span>
              <div className="text-500 mt-2">
                Enterprise deployment and status tracking metrics
              </div>
            </div>
          </div>
        </div>

        {/* Primary Metrics */}
        <div className="col-12 md:col-4">
          <div className="surface-card shadow-2 p-3 border-round">
            <div className="flex justify-content-between mb-3">
              <div>
                <span className="block text-500 font-medium mb-3">
                  Total Issued
                </span>
                <div className="text-900 font-bold text-2xl">
                  {loading ? (
                    <Skeleton width="5rem" height="1.8rem" />
                  ) : (
                    formatNumber(data?.license.total_issued)
                  )}
                </div>
              </div>
              <div
                className="flex align-items-center justify-content-center bg-blue-100 border-round"
                style={{ width: "2.5rem", height: "2.5rem" }}
              >
                <i className="pi pi-id-card text-blue-500 text-xl"></i>
              </div>
            </div>
            <span className="text-500">Global License Pool</span>
          </div>
        </div>

        <div className="col-12 md:col-4">
          <div className="surface-card shadow-2 p-3 border-round">
            <div className="flex justify-content-between mb-3">
              <div>
                <span className="block text-500 font-medium mb-3">
                  Used / Onboarded
                </span>
                <div className="text-900 font-bold text-2xl">
                  {loading ? (
                    <Skeleton width="5rem" height="1.8rem" />
                  ) : (
                    formatNumber(data?.license.total_onboarded)
                  )}
                </div>
              </div>
              <div
                className="flex align-items-center justify-content-center bg-green-100 border-round"
                style={{ width: "2.5rem", height: "2.5rem" }}
              >
                <i className="pi pi-check-circle text-green-500 text-xl"></i>
              </div>
            </div>
            {loading ? (
              <Skeleton width="8rem" height="1rem" />
            ) : (
              <>
                <span className="text-green-500 font-medium">
                  {usagePercentage}%{" "}
                </span>
                <span className="text-500">Utilization</span>
              </>
            )}
          </div>
        </div>

        <div className="col-12 md:col-4">
          <div className="surface-card shadow-2 p-3 border-round">
            <div className="flex justify-content-between mb-3">
              <div>
                <span className="block text-500 font-medium mb-3">
                  Remaining
                </span>
                <div className="text-900 font-bold text-2xl">
                  {loading ? (
                    <Skeleton width="5rem" height="1.8rem" />
                  ) : (
                    formatNumber(data?.license.remaining)
                  )}
                </div>
              </div>
              <div
                className="flex align-items-center justify-content-center bg-orange-100 border-round"
                style={{ width: "2.5rem", height: "2.5rem" }}
              >
                <i className="pi pi-box text-orange-500 text-xl"></i>
              </div>
            </div>
            <span className="text-500">Unused and Expired</span>
          </div>
        </div>

        {/* Utilization Chart */}
        <div className="col-12 lg:col-8 mt-4">
          <div className="surface-card shadow-2 p-4 border-round h-full">
            <div className="text-900 text-xl font-medium mb-4">
              Utilization Distribution
            </div>
            <div style={{ height: "350px", position: "relative" }}>
              {loading ? (
                <div className="flex align-items-center justify-content-center h-full">
                  <Skeleton shape="circle" size="250px" />
                </div>
              ) : (
                <>
                  <Chart
                    type="doughnut"
                    data={distributionData}
                    options={commonOptions}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                    }}
                  >
                    <div className="text-900 font-bold text-3xl">
                      {usagePercentage}%
                    </div>
                    <div className="text-500 font-medium">Used</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* THE REPLACED ELEGANT LICENSE STATUS CARD */}
        <div className="col-12 lg:col-4 mt-4">
          <div
            className={`surface-card shadow-2 p-4 border-round h-full border-top-3 ${isExpired ? "border-red-500" : "border-blue-500"}`}
          >
            <div className="flex align-items-center justify-content-between mb-4">
              <span className="text-900 text-xl font-medium">
                License Integrity
              </span>
              {loading ? (
                <Skeleton width="4rem" height="1.5rem" />
              ) : (
                <div
                  className={`${isExpired ? "bg-red-100" : "bg-green-100"} px-2 py-1 border-round`}
                >
                  <span
                    className={`${isExpired ? "text-red-700" : "text-green-700"} text-xs font-bold uppercase`}
                  >
                    {isExpired ? "Expired" : "Active"}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-column align-items-center py-4">
              <div
                className={`relative flex align-items-center justify-content-center border-circle ${isExpired ? "bg-red-50" : "bg-green-50"} mb-4`}
                style={{ width: "100px", height: "100px" }}
              >
                {loading ? (
                  <Skeleton shape="circle" size="100px" />
                ) : (
                  <i
                    className={`pi ${isExpired ? "pi-exclamation-triangle text-red-500" : "pi-verified text-green-500"}`}
                    style={{ fontSize: "3.5rem" }}
                  ></i>
                )}
                <div
                  className={`absolute border-circle ${isExpired ? "border-red-200" : "border-green-200"}`}
                  style={{
                    width: "120px",
                    height: "120px",
                    borderWidth: "2px",
                    borderStyle: "dashed",
                    opacity: 0.6,
                  }}
                ></div>
              </div>

              <div className="text-center w-full">
                <div className="grid grid-nogutter mt-2">
                  <div className="col-6 border-right-1 surface-border">
                    <span className="text-500 text-xs uppercase font-bold tracking-wider block mb-1">
                      Issue Date
                    </span>
                    <span className="text-900 font-bold text-lg block">
                      {loading ? (
                        <Skeleton
                          width="5rem"
                          height="1.2rem"
                          className="mx-auto"
                        />
                      ) : (
                        data?.integrity.issue_date
                      )}
                    </span>
                  </div>
                  <div className="col-6">
                    <span className="text-500 text-xs uppercase font-bold tracking-wider block mb-1">
                      Expiry Date
                    </span>
                    <span className="text-900 font-bold text-lg block">
                      {loading ? (
                        <Skeleton
                          width="5rem"
                          height="1.2rem"
                          className="mx-auto"
                        />
                      ) : (
                        data?.integrity.expiry_date
                      )}
                    </span>
                  </div>
                </div>

                <div className="surface-50 p-3 border-round mt-4 flex align-items-center justify-content-center gap-2">
                  <i className="pi pi-shield text-blue-500"></i>
                  <span className="text-700 font-medium text-sm">
                    {loading ? (
                      <Skeleton width="10rem" height="1rem" />
                    ) : data?.integrity.security_verified ? (
                      "Enterprise Security Verified"
                    ) : (
                      "Security Alert"
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-4 border-top-1 surface-border">
              <div className="flex justify-content-between text-sm">
                <span className="text-500 font-medium">Status Check</span>
                {loading ? (
                  <Skeleton width="4rem" height="1rem" />
                ) : (
                  <span
                    className={`${isExpired ? "text-red-600" : "text-green-600"} font-bold`}
                  >
                    {data?.integrity.action_required
                      ? "Action Required"
                      : "Passed"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Devices Table */}
      <div className="grid mt-4">
        <div className="col-12">
          <div className="surface-card shadow-2 p-4 border-round">
            <div className="flex align-items-center justify-content-between mb-4">
              <span className="text-900 text-xl font-medium">
                Recent Device Activity
              </span>
              <button
                onClick={() => navigate("/view-device")}
                className="p-button p-button-text p-button-sm text-green-500 font-bold uppercase tracking-wider"
              >
                View All
              </button>
            </div>
            {devicesLoading ? (
              <div className="flex flex-column gap-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} height="2.5rem" />
                ))}
              </div>
            ) : (
              <DataTable
                value={recentDevices}
                responsiveLayout="stack"
                breakpoint="960px"
                size="small"
                className="p-datatable-sm"
                emptyMessage="No devices found"
              >
                <Column
                  header="S/N"
                  body={serialNumberTemplate}
                  className="font-medium"
                  style={{ minWidth: "5rem" }}
                ></Column>
                <Column
                  field="imei"
                  header="IMEI"
                  className="font-medium"
                  style={{ minWidth: "12rem" }}
                ></Column>
                <Column
                  field="serial_number"
                  header="Serial Number"
                  style={{ minWidth: "12rem" }}
                ></Column>
                <Column field="model" header="Model"></Column>
                <Column field="color" header="Color"></Column>
                <Column field="status" header="Status"></Column>
                <Column
                  field="last_seen"
                  header="Last Activity"
                  className="text-500"
                ></Column>
              </DataTable>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
